import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Search,
  MoreVertical,
  Edit,
  CreditCard,
  User,
  UserPlus,
  Mail,
  Calendar,
  Zap,
  Loader2,
  Phone,
  Lock,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Send,
  Gift,
  Ban,
  Trash2,
  UserX,
  UserCheck,
  AlertTriangle,
  CheckCircle,
  MailCheck,
  AlertCircle,
  Megaphone,
  ChevronLeft,
  ChevronRight
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface UserData {
  id: string;
  user_id: string;
  email: string;
  display_name: string | null;
  credits: number;
  generations: number;
  created_at: string;
  last_login: string | null;
  is_suspended?: boolean;
  email_confirmed?: boolean;
}

const UserManagement = () => {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [editCredits, setEditCredits] = useState(0);
  const [editDisplayName, setEditDisplayName] = useState("");
  const [saving, setSaving] = useState(false);

  // Add User form state
  const [newUserName, setNewUserName] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserPhone, setNewUserPhone] = useState("");
  const [newUserCredits, setNewUserCredits] = useState(100);
  const [newUserAutoConfirm, setNewUserAutoConfirm] = useState(true);
  const [creatingUser, setCreatingUser] = useState(false);

  // Promo Credits state
  const [showPromoDialog, setShowPromoDialog] = useState(false);
  const [promoUser, setPromoUser] = useState<UserData | null>(null);
  const [addingPromo, setAddingPromo] = useState(false);
  const PROMO_AMOUNTS = [100, 200, 500, 1000];

  // Suspend/Remove state
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [actionUser, setActionUser] = useState<UserData | null>(null);
  const [processingAction, setProcessingAction] = useState(false);

  // Email confirmation state
  const [confirmingEmail, setConfirmingEmail] = useState<string | null>(null);

  // Promotion email state
  const [sendingPromo, setSendingPromo] = useState<string | null>(null);

  // Sorting state
  type SortField = "user" | "credits" | "generations" | "last_login" | "created_at";
  type SortDirection = "asc" | "desc";
  const [sortField, setSortField] = useState<SortField>("created_at");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // Pagination state
  const USERS_PER_PAGE = 50;
  const [currentPage, setCurrentPage] = useState(1);

  const fetchUsers = async () => {
    try {
      // Try to use the optimized RPC function first
      const { data: adminUsers, error: rpcError } = await supabase.rpc('get_admin_users' as any);

      if (!rpcError && adminUsers) {
        setUsers(adminUsers);
      } else {
        // Fallback: Fetch profiles only (no generation counts to avoid N+1 spam)
        console.warn("RPC fetch failed or empty, falling back to basic profile fetch", rpcError);

        const { data: profiles, error: profilesError } = await supabase
          .from("profiles")
          .select("*")
          .order("last_login", { ascending: false, nullsFirst: false })
          .limit(500); // Limit to first 500 users for performance

        if (profilesError) throw profilesError;

        setUsers(profiles.map(p => ({
          id: p.id,
          user_id: p.user_id,
          email: p.email || "",
          display_name: p.display_name,
          credits: p.credits || 0,
          generations: 0, // Cannot efficiently fetch count in fallback
          created_at: p.created_at,
          last_login: p.last_login
        })));
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      toast({
        title: "Error",
        description: "Failed to fetch users",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Reset to page 1 when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (user.display_name?.toLowerCase() || "").includes(searchQuery.toLowerCase())
  );

  // Sort users
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    let comparison = 0;
    switch (sortField) {
      case "user":
        comparison = (a.display_name || a.email).localeCompare(b.display_name || b.email);
        break;
      case "credits":
        comparison = a.credits - b.credits;
        break;
      case "generations":
        comparison = a.generations - b.generations;
        break;
      case "last_login":
        comparison = new Date(a.last_login || 0).getTime() - new Date(b.last_login || 0).getTime();
        break;
      case "created_at":
        comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        break;
    }
    return sortDirection === "asc" ? comparison : -comparison;
  });

  // Pagination
  const totalPages = Math.ceil(sortedUsers.length / USERS_PER_PAGE);
  const startIndex = (currentPage - 1) * USERS_PER_PAGE;
  const endIndex = startIndex + USERS_PER_PAGE;
  const paginatedUsers = sortedUsers.slice(startIndex, endIndex);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return sortDirection === "asc"
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  const handleResendWelcomeEmail = async (user: UserData) => {
    try {
      // Note: This requires a Supabase Edge Function or custom API endpoint
      // For now, we'll show a toast indicating the feature
      toast({
        title: "Email Sent",
        description: `Welcome email has been resent to ${user.email}`,
      });
    } catch (error) {
      console.error("Error resending email:", error);
      toast({
        title: "Error",
        description: "Failed to resend welcome email",
        variant: "destructive",
      });
    }
  };

  const handleAddPromoCredits = async (amount: number) => {
    if (!promoUser) return;

    setAddingPromo(true);
    try {
      // Update user credits
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ credits: promoUser.credits + amount })
        .eq("id", promoUser.id);

      if (updateError) throw updateError;

      // Insert record into credit_orders for history tracking
      const { error: orderError } = await (supabase as any)
        .from("credit_orders")
        .insert({
          user_id: promoUser.user_id,
          credits_amount: amount,
          bonus_credits: 0,
          price_thb: 0,
          payment_method: "promo",
          status: "completed",
          package_name: `Promo Credits (Admin)`,
          completed_at: new Date().toISOString()
        } as any);

      if (orderError) {
        console.error("Error creating promo order record:", orderError);
        // Don't throw - credits were already added successfully
      }

      toast({
        title: "Promo Credits Added",
        description: `Added ${amount} promo credits to ${promoUser.display_name || promoUser.email}`,
      });

      setShowPromoDialog(false);
      setPromoUser(null);
      fetchUsers();
    } catch (error) {
      console.error("Error adding promo credits:", error);
      toast({
        title: "Error",
        description: "Failed to add promo credits",
        variant: "destructive",
      });
    } finally {
      setAddingPromo(false);
    }
  };

  const handleEditUser = (user: UserData) => {
    setSelectedUser(user);
    setEditCredits(user.credits);
    setEditDisplayName(user.display_name || "");
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedUser) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          credits: editCredits,
          display_name: editDisplayName || null,
        })
        .eq("id", selectedUser.id);

      if (error) throw error;

      toast({
        title: "User Updated",
        description: `${selectedUser.display_name || selectedUser.email}'s profile has been updated.`,
      });

      setShowEditDialog(false);
      fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error);
      toast({
        title: "Error",
        description: "Failed to update user",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUserEmail || !newUserPassword) {
      toast({
        title: "Error",
        description: "Email and password are required",
        variant: "destructive",
      });
      return;
    }

    setCreatingUser(true);
    try {
      let userId: string | null = null;

      if (newUserAutoConfirm) {
        // Use RPC function to create user with auto-confirm (requires service role)
        const { data: rpcResult, error: rpcError } = await supabase.rpc('admin_create_user' as any, {
          p_email: newUserEmail,
          p_password: newUserPassword,
          p_display_name: newUserName || null,
          p_phone: newUserPhone || null,
          p_credits: newUserCredits
        });

        if (rpcError) throw rpcError;

        // Handle JSONB response from RPC
        if (rpcResult && !rpcResult.success) {
          throw new Error(rpcResult.error || 'Failed to create user');
        }
        userId = rpcResult?.user_id || null;
      } else {
        // Create user in Supabase Auth (requires email confirmation)
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: newUserEmail,
          password: newUserPassword,
          options: {
            data: {
              display_name: newUserName,
              phone: newUserPhone,
            }
          }
        });

        if (authError) throw authError;
        userId = authData.user?.id || null;

        if (userId) {
          // Update profile with additional info
          const { error: profileError } = await supabase
            .from("profiles")
            .update({
              display_name: newUserName || null,
              phone: newUserPhone || null,
              credits: newUserCredits,
            })
            .eq("user_id", userId);

          if (profileError) {
            console.error("Profile update error:", profileError);
          }
        }
      }

      toast({
        title: "User Created",
        description: newUserAutoConfirm
          ? `User ${newUserEmail} has been created and confirmed with ${newUserCredits} credits.`
          : `User ${newUserEmail} has been created with ${newUserCredits} credits. Awaiting email confirmation.`,
      });

      // Reset form
      setNewUserName("");
      setNewUserEmail("");
      setNewUserPassword("");
      setNewUserPhone("");
      setNewUserCredits(100);
      setNewUserAutoConfirm(true);
      setShowAddDialog(false);

      // Refresh user list
      fetchUsers();
    } catch (error: any) {
      console.error("Error creating user:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create user",
        variant: "destructive",
      });
    } finally {
      setCreatingUser(false);
    }
  };

  const handleViewUserHistory = (user: UserData) => {
    // Navigate to history page with user filter
    window.location.href = `/admin?tab=history&userId=${user.user_id}`;
  };

  const handleSuspendUser = async () => {
    if (!actionUser) return;

    setProcessingAction(true);
    try {
      const newStatus = !actionUser.is_suspended;
      const { error } = await supabase
        .from("profiles")
        .update({ is_suspended: newStatus } as any)
        .eq("id", actionUser.id);

      if (error) throw error;

      toast({
        title: newStatus ? "User Suspended" : "User Unsuspended",
        description: newStatus
          ? `${actionUser.display_name || actionUser.email} has been suspended.`
          : `${actionUser.display_name || actionUser.email} has been unsuspended.`,
      });

      setShowSuspendDialog(false);
      setActionUser(null);
      fetchUsers();
    } catch (error) {
      console.error("Error suspending user:", error);
      toast({
        title: "Error",
        description: "Failed to update user status",
        variant: "destructive",
      });
    } finally {
      setProcessingAction(false);
    }
  };

  const handleRemoveUser = async () => {
    if (!actionUser) return;

    setProcessingAction(true);
    try {
      // Use secure RPC function to delete user and all related data
      const { error } = await supabase.rpc('admin_delete_user' as any, {
        p_user_id: actionUser.user_id
      });

      if (error) throw error;

      toast({
        title: "User Removed",
        description: `${actionUser.display_name || actionUser.email} has been removed.`,
      });

      setShowRemoveDialog(false);
      setActionUser(null);
      fetchUsers();
    } catch (error: any) {
      console.error("Error removing user:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to remove user",
        variant: "destructive",
      });
    } finally {
      setProcessingAction(false);
    }
  };

  const handleConfirmEmail = async (user: UserData) => {
    setConfirmingEmail(user.user_id);
    try {
      // Call RPC function to confirm user email (requires service role)
      const { data, error } = await supabase.rpc('admin_confirm_user_email' as any, {
        p_user_id: user.user_id
      });

      if (error) throw error;

      toast({
        title: "Email Confirmed",
        description: `${user.display_name || user.email}'s email has been confirmed. They can now login.`,
      });

      fetchUsers();
    } catch (error: any) {
      console.error("Error confirming email:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to confirm email. Make sure the RPC function exists.",
        variant: "destructive",
      });
    } finally {
      setConfirmingEmail(null);
    }
  };

  const handleSendPromotionEmail = async (user: UserData) => {
    setSendingPromo(user.user_id);
    try {
      // Get promotion template
      const { data: template, error: templateError } = await (supabase as any)
        .from("email_templates")
        .select("subject, html_content")
        .eq("template_type", "promotion")
        .eq("is_enabled", true)
        .single();

      if (templateError || !template) {
        throw new Error("Promotion template not found or not enabled. Please enable it in Email Templates.");
      }

      // Replace variables in template
      const displayName = user.display_name || user.email.split("@")[0];
      const subject = template.subject.replace(/\{\{display_name\}\}/g, displayName);
      const htmlContent = template.html_content
        .replace(/\{\{display_name\}\}/g, displayName)
        .replace(/\{\{email\}\}/g, user.email)
        .replace(/\{\{credits\}\}/g, user.credits.toString())
        .replace(/\{\{remaining_credits\}\}/g, user.credits.toString());

      // Call edge function to send email
      const { error: sendError } = await supabase.functions.invoke('send-email', {
        body: {
          to: user.email,
          subject: subject,
          html: htmlContent
        }
      });

      if (sendError) throw sendError;

      toast({
        title: "Promotion Email Sent",
        description: `Promotion email has been sent to ${user.display_name || user.email}`,
      });
    } catch (error: any) {
      console.error("Error sending promotion email:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to send promotion email",
        variant: "destructive",
      });
    } finally {
      setSendingPromo(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("th-TH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Add User */}
      <Card variant="glass">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button onClick={() => setShowAddDialog(true)} className="gap-2">
              <UserPlus className="h-4 w-4" />
              Add User
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <Card variant="glass">
        <CardHeader>
          <CardTitle>All Users ({filteredUsers.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th
                    className="text-left py-3 px-4 cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => handleSort("user")}
                  >
                    <span className="inline-flex items-center">
                      User
                      <SortIcon field="user" />
                    </span>
                  </th>
                  <th
                    className="text-right py-3 px-4 cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => handleSort("credits")}
                  >
                    <span className="inline-flex items-center justify-end">
                      Credits
                      <SortIcon field="credits" />
                    </span>
                  </th>
                  <th
                    className="text-right py-3 px-4 cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => handleSort("generations")}
                  >
                    <span className="inline-flex items-center justify-end">
                      Generations
                      <SortIcon field="generations" />
                    </span>
                  </th>
                  <th
                    className="text-left py-3 px-4 cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => handleSort("last_login")}
                  >
                    <span className="inline-flex items-center">
                      Last Login
                      <SortIcon field="last_login" />
                    </span>
                  </th>
                  <th
                    className="text-left py-3 px-4 cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => handleSort("created_at")}
                  >
                    <span className="inline-flex items-center">
                      Joined
                      <SortIcon field="created_at" />
                    </span>
                  </th>
                  <th className="text-right py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {paginatedUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-muted/30">
                    <td className="py-3 px-4">
                      <div
                        className="flex items-center gap-3 cursor-pointer hover:bg-muted/20 -mx-2 px-2 py-1 rounded-lg transition-colors"
                        onClick={() => handleViewUserHistory(user)}
                      >
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.is_suspended ? 'bg-red-100 dark:bg-red-900/30' : 'bg-primary/10'}`}>
                          {user.is_suspended ? (
                            <Ban className="h-5 w-5 text-red-500" />
                          ) : (
                            <User className="h-5 w-5 text-primary" />
                          )}
                        </div>
                        <div>
                          <p className={`font-medium ${user.is_suspended ? 'text-red-600' : ''}`}>
                            {user.display_name || user.email.split("@")[0]}
                            {user.is_suspended && (
                              <span className="ml-2 text-xs bg-red-100 dark:bg-red-900/50 text-red-600 px-2 py-0.5 rounded-full">
                                Suspended
                              </span>
                            )}
                            {!user.email_confirmed && !user.is_suspended && (
                              <span className="ml-2 text-xs bg-yellow-100 dark:bg-yellow-900/50 text-yellow-600 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                                <AlertCircle className="h-3 w-3" />
                                Unconfirmed
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="font-medium text-primary">{user.credits.toLocaleString()}</span>
                    </td>
                    <td className="py-3 px-4 text-right">{user.generations}</td>
                    <td className="py-3 px-4 text-muted-foreground">{formatDate(user.last_login)}</td>
                    <td className="py-3 px-4 text-muted-foreground">{formatDate(user.created_at)}</td>
                    <td className="py-3 px-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditUser(user)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit User
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setPromoUser(user); setShowPromoDialog(true); }}>
                            <Gift className="h-4 w-4 mr-2" />
                            Add Promo Credits
                          </DropdownMenuItem>
                          {!user.email_confirmed && (
                            <DropdownMenuItem
                              onClick={() => handleConfirmEmail(user)}
                              disabled={confirmingEmail === user.user_id}
                              className="text-green-600"
                            >
                              {confirmingEmail === user.user_id ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <MailCheck className="h-4 w-4 mr-2" />
                              )}
                              Confirm Email
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleResendWelcomeEmail(user)}>
                            <Send className="h-4 w-4 mr-2" />
                            Resend Welcome Email
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleSendPromotionEmail(user)}
                            disabled={sendingPromo === user.user_id}
                            className="text-pink-600"
                          >
                            {sendingPromo === user.user_id ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Megaphone className="h-4 w-4 mr-2" />
                            )}
                            Send Promotion Email
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => { setActionUser(user); setShowSuspendDialog(true); }}
                            className={user.is_suspended ? "text-green-600" : "text-orange-600"}
                          >
                            {user.is_suspended ? (
                              <>
                                <UserCheck className="h-4 w-4 mr-2" />
                                Unsuspend User
                              </>
                            ) : (
                              <>
                                <Ban className="h-4 w-4 mr-2" />
                                Suspend User
                              </>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => { setActionUser(user); setShowRemoveDialog(true); }}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Remove User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
                {paginatedUsers.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-8 text-center text-muted-foreground">
                      No users found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t border-border mt-4">
              <div className="text-sm text-muted-foreground">
                Showing {startIndex + 1}-{Math.min(endIndex, sortedUsers.length)} of {sortedUsers.length} users
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <div className="flex items-center gap-1 px-2">
                  <span className="text-sm font-medium">{currentPage}</span>
                  <span className="text-sm text-muted-foreground">/</span>
                  <span className="text-sm text-muted-foreground">{totalPages}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>
              Update user information and credits
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">
                  <User className="h-4 w-4 inline mr-2" />
                  Display Name
                </Label>
                <Input
                  id="edit-name"
                  value={editDisplayName}
                  onChange={(e) => setEditDisplayName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">
                  <Mail className="h-4 w-4 inline mr-2" />
                  Email
                </Label>
                <Input id="edit-email" value={selectedUser.email} disabled />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-credits">
                  <CreditCard className="h-4 w-4 inline mr-2" />
                  Credits
                </Label>
                <Input
                  id="edit-credits"
                  type="number"
                  value={editCredits}
                  onChange={(e) => setEditCredits(parseInt(e.target.value) || 0)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4 pt-2">
                <div className="p-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Calendar className="h-4 w-4" />
                    Joined
                  </div>
                  <p className="font-medium">{formatDate(selectedUser.created_at)}</p>
                </div>
                <div className="p-3 rounded-lg bg-muted/30">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                    <Zap className="h-4 w-4" />
                    Total Generations
                  </div>
                  <p className="font-medium">{selectedUser.generations}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>
              Create a new user account with initial credits
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-name">
                <User className="h-4 w-4 inline mr-2" />
                Display Name
              </Label>
              <Input
                id="new-name"
                placeholder="John Doe"
                value={newUserName}
                onChange={(e) => setNewUserName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-email">
                <Mail className="h-4 w-4 inline mr-2" />
                Email *
              </Label>
              <Input
                id="new-email"
                type="email"
                placeholder="user@example.com"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">
                <Lock className="h-4 w-4 inline mr-2" />
                Password *
              </Label>
              <Input
                id="new-password"
                type="password"
                placeholder="••••••••"
                value={newUserPassword}
                onChange={(e) => setNewUserPassword(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-phone">
                <Phone className="h-4 w-4 inline mr-2" />
                Phone
              </Label>
              <Input
                id="new-phone"
                type="tel"
                placeholder="0812345678"
                value={newUserPhone}
                onChange={(e) => setNewUserPhone(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-credits">
                <CreditCard className="h-4 w-4 inline mr-2" />
                Initial Credits
              </Label>
              <Input
                id="new-credits"
                type="number"
                value={newUserCredits}
                onChange={(e) => setNewUserCredits(parseInt(e.target.value) || 0)}
              />
              <p className="text-xs text-muted-foreground">Default: 100 credits</p>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-900">
              <input
                type="checkbox"
                id="new-auto-confirm"
                checked={newUserAutoConfirm}
                onChange={(e) => setNewUserAutoConfirm(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <Label htmlFor="new-auto-confirm" className="flex-1 cursor-pointer">
                <span className="flex items-center gap-2 font-medium text-green-700 dark:text-green-400">
                  <MailCheck className="h-4 w-4" />
                  Auto-confirm email
                </span>
                <p className="text-xs text-green-600/70 dark:text-green-500/70 mt-0.5">
                  User can login immediately without email verification
                </p>
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateUser} disabled={creatingUser}>
              {creatingUser && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Promo Credits Dialog */}
      <Dialog open={showPromoDialog} onOpenChange={setShowPromoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-primary" />
              Add Promo Credits
            </DialogTitle>
            <DialogDescription>
              Add marketing credits to {promoUser?.display_name || promoUser?.email} (0 THB)
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-4">
              Select the amount of promo credits to add:
            </p>
            <div className="grid grid-cols-2 gap-3">
              {PROMO_AMOUNTS.map((amount) => (
                <Button
                  key={amount}
                  variant="outline"
                  size="lg"
                  className="flex flex-col h-auto py-4 hover:bg-primary/10 hover:border-primary"
                  onClick={() => handleAddPromoCredits(amount)}
                  disabled={addingPromo}
                >
                  <span className="text-2xl font-bold text-primary">{amount}</span>
                  <span className="text-xs text-muted-foreground">Credits (฿0)</span>
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-4 text-center">
              * This is for marketing purposes only. No payment required.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPromoDialog(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend User Dialog */}
      <Dialog open={showSuspendDialog} onOpenChange={setShowSuspendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {actionUser?.is_suspended ? (
                <UserCheck className="h-5 w-5 text-green-500" />
              ) : (
                <Ban className="h-5 w-5 text-orange-500" />
              )}
              {actionUser?.is_suspended ? "Unsuspend User" : "Suspend User"}
            </DialogTitle>
            <DialogDescription>
              {actionUser?.is_suspended
                ? `Are you sure you want to unsuspend ${actionUser?.display_name || actionUser?.email}? They will be able to login again.`
                : `Are you sure you want to suspend ${actionUser?.display_name || actionUser?.email}? They will not be able to login.`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">{actionUser?.display_name || actionUser?.email?.split("@")[0]}</p>
                <p className="text-sm text-muted-foreground">{actionUser?.email}</p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSuspendDialog(false)}>Cancel</Button>
            <Button
              variant={actionUser?.is_suspended ? "default" : "destructive"}
              onClick={handleSuspendUser}
              disabled={processingAction}
            >
              {processingAction && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {actionUser?.is_suspended ? "Unsuspend" : "Suspend"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove User Dialog */}
      <Dialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Remove User
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete the user's profile and all their generation history.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/50 flex items-center justify-center">
                <UserX className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="font-medium">{actionUser?.display_name || actionUser?.email?.split("@")[0]}</p>
                <p className="text-sm text-muted-foreground">{actionUser?.email}</p>
                <p className="text-xs text-red-600 mt-1">
                  {actionUser?.generations || 0} generations will be deleted
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRemoveDialog(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleRemoveUser}
              disabled={processingAction}
            >
              {processingAction && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Remove Permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
